import { AnalyticsEvent } from '../AnalyticsService';
import { CollectorEvent, CollectorName } from './types';
import { collectDeviceInfo, DeviceInfo } from './device-info';
import { eventTypes } from '@utils/constants.ts';

const SESSION_ID_KEY = 'ec_session_id';
const SDK_VERSION = '1.0.0';
const SCHEMA_VERSION = '1';

let cachedAaid: string | undefined;
let cachedClientId: string | undefined;
let cachedMpid: string | undefined;
let cachedDeviceInfo: DeviceInfo = {};
let cachedPartner: string | undefined;
let cachedEnvironment: string | undefined;

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

async function hashSha512(value: string): Promise<string> {
  const encoded = new TextEncoder().encode(value);
  const buffer = await crypto.subtle.digest('SHA-512', encoded);
  return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');
}

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

function sanitizePageUrl(href: string): string {
  try {
    const url = new URL(href);
    ['aaid', 'mpid'].forEach((p) => url.searchParams.delete(p));
    return url.toString();
  } catch {
    return href;
  }
}

function buildProperties(
  params: Record<string, unknown>,
  deviceInfo: DeviceInfo,
  partner: string | undefined,
  environment: string | undefined,
  clientId: string | undefined,
): Record<string, unknown> {
  const eventProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    // partnerId comes from RUNTIME_CONFIG, not the event; language collision: device language wins
    if (key === 'language' || key === 'partnerId') continue;
    const snakeKey = key === 'type' ? 'link_type' : camelToSnake(key);
    eventProps[snakeKey] = value;
  }

  const referrerDomain =
    typeof document !== 'undefined' && document.referrer
      ? (() => { try { return new URL(document.referrer).hostname; } catch { return undefined; } })()
      : undefined;

  const pageTitle = typeof document !== 'undefined' ? (document as any).title || undefined : undefined;

  return {
    ...(clientId ? { client_id: clientId } : {}),
    partner_id: partner,
    environment,
    ...deviceInfo,
    referrer_domain: referrerDomain,
    page_title: pageTitle,
    sdk_version: SDK_VERSION,
    schema_version: SCHEMA_VERSION,
    ...eventProps,
  };
}

export async function initEventMapper(): Promise<void> {
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const params = new URLSearchParams(search);

  const aaid = params.get('aaid');
  if (aaid) {
    cachedAaid = aaid;
    cachedClientId = await hashSha512(aaid);
  }

  const mpid = params.get('mpid');
  if (mpid) {
    cachedMpid = mpid;
  }

  const rc = typeof window !== 'undefined' ? (window as any).__RUNTIME_CONFIG__ : undefined;
  cachedPartner = rc?.property || undefined;
  cachedEnvironment = rc?.appEnv || 'production';

  cachedDeviceInfo = await collectDeviceInfo();
}

export function resetMapperForTesting(): void {
  cachedAaid = undefined;
  cachedClientId = undefined;
  cachedMpid = undefined;
  cachedDeviceInfo = {};
  cachedPartner = undefined;
  cachedEnvironment = undefined;
}

export function mapToCollectorEvent(analyticsEvent: AnalyticsEvent): CollectorEvent {
  // errorMessage/stackTrace are lifted to top-level CollectorEvent fields for error
  // events; errorType/source stay in `rest` so buildProperties emits error_type/source.
  const { searchParams, eventCallback, eventTimeout, contentId, content_id, errorMessage, stackTrace, ...rest } =
    analyticsEvent.params || {};

  const isError = analyticsEvent.name === eventTypes.ERROR;

  return {
    event_type: analyticsEvent.name,
    time_generated: new Date().toISOString(),
    session_id: getSessionId(),
    page_url: typeof window !== 'undefined' ? sanitizePageUrl(window.location.href) : undefined,
    referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
    content_id: contentId ?? content_id,
    mpid: cachedMpid,
    aaid: cachedAaid,
    ...(isError ? { error_message: errorMessage as string | undefined, stack_trace: stackTrace as string | undefined } : {}),
    properties: buildProperties(rest, cachedDeviceInfo, cachedPartner, cachedEnvironment, cachedClientId),
  };
}

export function selectCollector(event: AnalyticsEvent): CollectorName {
  return event.name === eventTypes.ERROR ? 'errors' : 'analytics';
}
